/**
 * Configuration for WebSocket connections
 * Values can be overridden in environment-specific configurations
 * 
 * Supports dual authentication for both Cognito and Federate tokens
 */

import { tokenManager } from '../services/tokenManager';
import { deviceDetectionService } from '../services/deviceDetectionService';

// Get WebSocket API URL from environment or default to the backend service URL
// This respects the manual local/beta switching you do without webpack config changes
const wsBaseUrl = process.env.REACT_APP_WS_API_URL || "https://beta.na.safety-genie-api.whs.amazon.dev";

/**
 * Returns the WebSocket base URL with protocol conversion (http->ws, https->wss)
 * WebSocket connections use a separate ALB with 'alb.' subdomain for non-local URLs
 */
export const getWebSocketUrl = (): string => {
  // Handle localhost and 127.0.0.1 URLs - no 'alb.' subdomain needed for local development
  if (wsBaseUrl.startsWith("http://localhost") || wsBaseUrl.startsWith("http://127.0.0.1")) {
    return wsBaseUrl.replace("http://", "ws://");
  }
  
  // Convert HTTP protocol to WebSocket protocol and add 'alb.' subdomain for remote URLs
  if (wsBaseUrl.startsWith("https://")) {
    // Replace https:// with wss:// and insert 'alb.' subdomain
    return wsBaseUrl.replace("https://", "wss://alb.");
  } else if (wsBaseUrl.startsWith("http://")) {
    // Replace http:// with ws:// and insert 'alb.' subdomain
    return wsBaseUrl.replace("http://", "ws://alb.");
  }
  
  // If no protocol specified, assume secure WebSocket with 'alb.' subdomain
  if (!wsBaseUrl.startsWith("ws://") && !wsBaseUrl.startsWith("wss://")) {
    return `wss://alb.${wsBaseUrl}`;
  }
  
  return wsBaseUrl;
};

/**
 * Generate a client ID for WebSocket connections
 */
export const generateClientId = (): string => {
  // Generate a unique identifier for this client session
  return `client-${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Get authentication parameters for WebSocket connections
 * Supports both Cognito and Federate authentication types
 */
export const getWebSocketAuthParams = async (): Promise<URLSearchParams> => {
  const params = new URLSearchParams();
  
  try {
    // Determine authentication type based on device type and stored tokens
    const deviceType = deviceDetectionService.getDeviceType();
    const storedAuthType = tokenManager.getStoredAuthType();
    
    // Use stored auth type if available, otherwise default based on device type
    const authType = storedAuthType || (deviceType === 'mobile' ? 'federate' : 'cognito');
    
    console.log(`Getting WebSocket auth params for ${authType} authentication (device: ${deviceType})`);
    
    // Get tokens for the determined auth type
    const tokens = tokenManager.getTokens(authType);
    
    if (!tokens) {
      console.warn(`No ${authType} tokens available for WebSocket authentication`);
      return params;
    }
    
    // Ensure tokens are valid and refresh if needed
    let validTokens;
    try {
      validTokens = await tokenManager.ensureValidTokens(authType);
    } catch (error) {
      console.warn(`Failed to validate ${authType} tokens for WebSocket:`, error);
      return params;
    }
    
    if (authType === 'federate') {
      // For federate tokens, use access_token query parameter
      params.append('access_token', validTokens.accessToken);
      params.append('id_token', validTokens.idToken);
    } else {
      // For Cognito tokens, use existing format
      params.append('Authorization', `Bearer ${validTokens.idToken}`);
    }
    
    console.log(`WebSocket auth params prepared for ${authType} authentication`);
    return params;
    
  } catch (error) {
    console.error('Error preparing WebSocket authentication parameters:', error);
    return params;
  }
};

/**
 * Get authentication headers for WebSocket connections
 * Alternative method that returns headers object instead of URL parameters
 */
export const getWebSocketAuthHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {};
  
  try {
    // Determine authentication type based on device type and stored tokens
    const deviceType = deviceDetectionService.getDeviceType();
    const storedAuthType = tokenManager.getStoredAuthType();
    
    // Use stored auth type if available, otherwise default based on device type
    const authType = storedAuthType || (deviceType === 'mobile' ? 'federate' : 'cognito');
    
    console.log(`Getting WebSocket auth headers for ${authType} authentication (device: ${deviceType})`);
    
    // Get tokens for the determined auth type
    const tokens = tokenManager.getTokens(authType);
    
    if (!tokens) {
      console.warn(`No ${authType} tokens available for WebSocket authentication`);
      return headers;
    }
    
    // Ensure tokens are valid and refresh if needed
    let validTokens;
    try {
      validTokens = await tokenManager.ensureValidTokens(authType);
    } catch (error) {
      console.warn(`Failed to validate ${authType} tokens for WebSocket:`, error);
      return headers;
    }
    
    if (authType === 'federate') {
      // For federate tokens, check device type
      if (deviceType === 'mobile') {
        // Mobile devices should NOT use headers for federate auth
        console.log('Mobile device detected - not setting WebSocket headers for federate auth');
        return headers;
      } else {
        // Desktop devices can use headers for federate auth
        headers['access_token'] = validTokens.accessToken;
        headers['id_token'] = validTokens.idToken;
      }
    } else {
      // For Cognito tokens, use existing format
      headers['Authorization'] = `Bearer ${validTokens.idToken}`;
    }
    
    console.log(`WebSocket auth headers prepared for ${authType} authentication`);
    return headers;
    
  } catch (error) {
    console.error('Error preparing WebSocket authentication headers:', error);
    return headers;
  }
};

export default {
  getWebSocketUrl,
  generateClientId,
  getWebSocketAuthParams,
  getWebSocketAuthHeaders
};
